import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useNavigation } from '../context/NavigationContext';
import { useMills } from '../context/MillsContext';

export default function Footer() {
  const { t, lang } = useLanguage();
  const { config } = useNavigation();
  const { principals, cfg: millCfg } = useMills();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/settings/public/contact_info').then(r => {
      if (r.data && Object.keys(r.data).length > 0) setContactInfo(r.data);
    }).catch(() => {});
    api.get('/settings/public/site_settings').then(r => {
      if (r.data && Object.keys(r.data).length > 0) setSiteSettings(r.data);
    }).catch(() => {});
    api.get('/catalog/categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const phone = contactInfo?.phone || '+971 4 357 9568';
  const emailAddr = contactInfo?.email || 'sales@axicor.ae';
  const address1 = contactInfo?.address_line1 || 'Unit 121, Building A5,';
  const address2 = contactInfo?.address_line2 || 'PO Box 390667, DWC HQ,';
  const city = contactInfo?.city || 'Dubai';
  const country = contactInfo?.country || 'UAE';
  const footerText = siteSettings?.footer_text || 'Leading supplier of specialty steel and alloy products, serving industries worldwide since 2014.';
  const websiteUrl = siteSettings?.website_url || 'www.axicor.ae';
  const copyrightText = siteSettings?.copyright_text || '';
  const erpUrl = millCfg?.erp_url || siteSettings?.erp_url || '';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) { toast.error('Please enter a valid email'); return; }
    setSubscribing(true);
    try {
      const res = await api.post('/newsletter/subscribe', { email: email.trim() });
      toast.success(res.data.message);
      setEmail('');
    } catch { toast.error('Something went wrong.'); }
    finally { setSubscribing(false); }
  };

  const colTitle = 'px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)] mb-7';
  const colLink = 'text-sm text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] transition-colors duration-400';

  return (
    <footer data-testid="main-footer" className="bg-[var(--px-surface)] border-t px-hair">
      {/* newsletter */}
      <div className="border-b px-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-20 items-end">
          <div>
            <p className="px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)] mb-5">
              {t('footer.newsletterKicker')}
            </p>
            <h3 className="px-display text-2xl md:text-4xl font-semibold tracking-tight text-[var(--px-ink)] max-w-xl leading-tight">
              {t('footer.newsletterHeading')}
            </h3>
          </div>
          <form onSubmit={handleSubscribe} noValidate data-testid="newsletter-form" className="w-full lg:w-[420px]">
            <div className="flex border-b px-hair focus-within:border-[var(--px-ink)] transition-colors duration-400">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('footer.newsletterPlaceholder')}
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent text-[var(--px-ink)] placeholder:text-[var(--px-ink-soft)] py-4 text-sm focus:outline-none"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-submit-btn"
                className="px-data group flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink)] disabled:opacity-40 ps-4"
              >
                {subscribing ? '...' : <>{t('footer.subscribe')}<ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1 rtl-flip" /></>}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* columns */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-14 lg:gap-10">
          <div>
            <img src="/brand/axicor-logo.png" alt="Axicor DWC LLC" className="h-11 w-auto mb-7" />
            <p className="text-sm text-[var(--px-ink-soft)] leading-relaxed mb-8 max-w-sm">{footerText}</p>
            <div className="space-y-3">
              <a href={`tel:${phone.replace(/\s/g, '')}`} data-testid="footer-phone" className={`flex items-center gap-3 ${colLink}`}>
                <Phone className="w-3.5 h-3.5 shrink-0" /> <span className="px-data text-xs">{phone}</span>
              </a>
              <a href={`mailto:${emailAddr}`} data-testid="footer-email" className={`flex items-center gap-3 ${colLink}`}>
                <Mail className="w-3.5 h-3.5 shrink-0" /> <span className="px-data text-xs">{emailAddr}</span>
              </a>
            </div>
            {siteSettings && (siteSettings.social_linkedin || siteSettings.social_twitter) && (
              <div className="flex gap-5 mt-7">
                {siteSettings.social_linkedin && (
                  <a href={siteSettings.social_linkedin} target="_blank" rel="noopener noreferrer" className="px-data text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] transition-colors">LinkedIn</a>
                )}
                {siteSettings.social_twitter && (
                  <a href={siteSettings.social_twitter} target="_blank" rel="noopener noreferrer" className="px-data text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] transition-colors">Twitter</a>
                )}
              </div>
            )}
          </div>

          {(config?.footer || []).map(col => (
            <div key={col.id || col.title} data-testid={`footer-col-${col.id || col.title}`}>
              <h4 className={colTitle}>{lang === 'ar' && col.title_ar ? col.title_ar : col.title}</h4>
              <ul className="space-y-3">
                {col.auto === 'categories'
                  ? categories.map(c => (
                      <li key={c.slug}><Link to={`/materials/${c.slug}`} className={colLink}>{c.name}</Link></li>
                    ))
                  : (col.items || []).filter(i => i.visible !== false).map(i => (
                      <li key={i.id || i.url}>
                        {i.new_tab
                          ? <a href={i.url} target="_blank" rel="noopener noreferrer" className={colLink}>{lang === 'ar' && i.label_ar ? i.label_ar : i.label}</a>
                          : <Link to={i.url} className={colLink}>{lang === 'ar' && i.label_ar ? i.label_ar : i.label}</Link>}
                      </li>
                    ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className={colTitle}>{t('footer.visitUs')}</h4>
            <div className="flex items-start gap-3 mb-7">
              <MapPin className="w-3.5 h-3.5 text-[var(--px-ink-soft)] mt-1 shrink-0" />
              <p className="text-sm text-[var(--px-ink-soft)] leading-relaxed">
                {address1}<br />{address2}<br />{city}, {country}
              </p>
            </div>
            <Link to="/contact" data-testid="footer-directions" className="px-data group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink)] border-b border-[var(--px-ink)] pb-1">
              {t('footer.getDirections')}
              <ArrowRight className="w-3 h-3 transition-transform duration-500 group-hover:translate-x-1 rtl-flip" />
            </Link>
          </div>
        </div>
      </div>

      {millCfg?.footer_line !== false && principals.length > 0 && (
        <div className="border-t px-hair" data-testid="footer-mill-line">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)]">
              {millCfg?.heading || t('mills.kicker')}
            </span>
            <Link to="/mills" data-testid="footer-mills-link"
              className="px-display text-sm font-semibold text-[var(--px-ink)] hover:text-[var(--px-accent)] transition-colors">
              {principals.map(m => m.name).join('  \u00B7  ')}
            </Link>
          </div>
        </div>
      )}

      {/* bottom bar */}
      <div className="bg-[var(--px-ink)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="px-data text-[10px] tracking-[0.15em] text-white/45">
            {copyrightText || `\u00A9 ${new Date().getFullYear()} Axicor DWC LLC. All rights reserved.`}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/legal/trademarks" data-testid="footer-trademark-link" className="px-data text-[10px] tracking-[0.15em] text-white/45 hover:text-white transition-colors">
              {t('footer.trademarks')}
            </Link>
            <a href={`https://${websiteUrl}`} className="px-data text-[10px] tracking-[0.15em] text-white/45 hover:text-white transition-colors">
              {websiteUrl}
            </a>
            {erpUrl && (
              <a
                href={erpUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-erp-link"
                className="px-data inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] text-white/45 hover:text-white transition-colors"
              >
                <LogIn className="w-3 h-3" /> {t('footer.staffErp')}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
